import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Gift, Check, Loader2, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('session_token')}`,
});

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch (_) {
    return '';
  }
};

const CodePromoRedeem = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    setAuthed(Boolean(localStorage.getItem('session_token')));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = (code || '').trim().toUpperCase();
    if (!trimmed) {
      setError('Entre un code pour continuer.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const r = await axios.post(`${API}/promo-code/redeem`, { code: trimmed }, { headers: authHeaders() });
      setSuccess(r.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Impossible de valider le code';
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0F0C] text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 text-center">
          <Gift className="mx-auto text-emerald-400 mb-4" size={40} />
          <h1 className="text-2xl font-bold mb-2">Un code promo à activer ?</h1>
          <p className="text-white/70 mb-6 text-sm">
            Connecte-toi ou crée un compte en 30&nbsp;secondes pour l'utiliser.
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={() => navigate('/?auth=register')}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full px-6 py-3 transition"
              data-testid="redeem-signup-btn"
            >
              Créer un compte
            </button>
            <button
              onClick={() => navigate('/?auth=login')}
              className="w-full bg-white/10 hover:bg-white/15 text-white font-semibold rounded-full px-6 py-3 transition"
              data-testid="redeem-login-btn"
            >
              Me connecter
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F0C] text-white flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full" data-testid="code-promo-redeem-page">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/30 mb-4">
            <Gift className="text-emerald-400" size={28} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Entre ton code
          </h1>
          <p className="text-white/60 mt-3 text-sm md:text-base">
            Débloque ton accès CabGolf en quelques secondes.
          </p>
        </div>

        {!success ? (
          <form
            onSubmit={submit}
            className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 md:p-8 space-y-4"
          >
            <label className="block">
              <span className="text-xs uppercase tracking-wider text-white/50">Code promo</span>
              <input
                autoFocus
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="EX: BIENVENUE7"
                className="mt-2 w-full bg-black/40 border border-white/15 focus:border-emerald-400/60 focus:ring-2 focus:ring-emerald-400/30 outline-none rounded-xl px-4 py-3 text-lg font-mono tracking-wider text-white placeholder:text-white/25"
                data-testid="redeem-code-input"
                disabled={busy}
                maxLength={40}
              />
            </label>

            {error && (
              <div
                className="bg-red-500/10 border border-red-500/30 text-red-200 text-sm rounded-xl px-4 py-3"
                data-testid="redeem-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || !code.trim()}
              className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-semibold rounded-full px-6 py-3 flex items-center justify-center gap-2 transition"
              data-testid="redeem-submit-btn"
            >
              {busy ? (
                <>
                  <Loader2 className="animate-spin" size={18} /> Validation…
                </>
              ) : (
                <>
                  Valider mon code <ArrowRight size={18} />
                </>
              )}
            </button>

            <p className="text-[11px] text-white/40 text-center">
              Un code = une utilisation par compte. Les codes expirent à la date définie par l'équipe CabGolf.
            </p>
          </form>
        ) : (
          <div
            className="bg-emerald-500/10 border border-emerald-400/30 rounded-2xl p-6 md:p-8 text-center"
            data-testid="redeem-success"
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500 text-black mb-4">
              <Check size={32} strokeWidth={3} />
            </div>
            <h2 className="text-2xl font-bold mb-2">Accès débloqué !</h2>
            <p className="text-white/80">
              Tu as <strong>{success.duration_days} jour{success.duration_days > 1 ? 's' : ''}</strong>{' '}
              d'accès gratuit à CabGolf.
            </p>
            <p className="text-white/60 text-sm mt-3">
              Expire le <strong className="text-emerald-300">{fmtDate(success.subscription_end)}</strong>.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-6 w-full bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-full px-6 py-3 inline-flex items-center justify-center gap-2 transition"
              data-testid="redeem-go-dashboard-btn"
            >
              Accéder à l'app <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CodePromoRedeem;
